import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CourseSearchApiService } from '../course-search-api.service';
import {
	updateCourseSearchError,
	updateCourses,
	fetchCourses,
	updateUniCourses,
	updateNotFoundError,
	fetchPaginatedCourses,
	updatePaginatedCourses,
	fetchUniCourses,
	fetchPaginatedUniCourses,
	updatePaginatedUniCourses,
	fetchClearingCourses,
	updateClearingCourses,
	fetchPaginatedClearingCourses,
	updatePaginatedClearingCourses,
	fetchClearingUniCourses,
	updateClearingUniCourses,
	fetchPaginatedClearingUniCourses,
	updatePaginatedClearingUniCourses,
} from './course-search.actions';
import {
	getStateClearingCourseSearchPayload,
	getStateClearingUniCourseSearchPayload,
	getStateCourseSearchPayload,
	getStateUniCourseSearchPayload,
} from './course-search.selectors';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';
import { GraphQLErrorCode } from '@uc/web/shared/data-models';
import { NotFoundErrorHandlerService } from '../../services/not-found-error-handler.service';

@Injectable()
export class StateCourseSearchEffects {
	constructor(
		private _actions$: Actions,
		private _store: Store,
		private _courseSrv: CourseSearchApiService,
		private _notFoundErrorHandler: NotFoundErrorHandlerService,
	) {}

	fetchCourses = createEffect(() => {
		return this._actions$.pipe(
			ofType(fetchCourses),
			withLatestFrom(this._store.select(getStateCourseSearchPayload)),
			switchMap(([, payload]) => this._courseSrv.getCourseList(payload)),
			map((courses) => updateCourses({ courses })),
			catchError((error) => {
				const code = error.networkError?.error?.data?.code;
				this._notFoundErrorHandler.handleError(code);
				if (code === GraphQLErrorCode.UNI_COURSES_404) {
					return of(updateNotFoundError(error));
				}

				return of(updateCourseSearchError({ error }));
			}),
		);
	});

	fetchPaginatedCourses = createEffect(() => {
		return this._actions$.pipe(
			ofType(fetchPaginatedCourses),
			withLatestFrom(this._store.select(getStateCourseSearchPayload)),
			switchMap(([, payload]) => this._courseSrv.getCourseList(payload)),
			map((courses) => updatePaginatedCourses({ courses })),
			catchError((error) => {
				const code = error.networkError.error?.data?.code;

				this._notFoundErrorHandler.handleError(code);
				if (code === GraphQLErrorCode.UNI_COURSES_404) {
					return of(updateNotFoundError(error));
				}
				return of(updateCourseSearchError({ error }));
			}),
		);
	});

	fetchUniCourses = createEffect(() => {
		return this._actions$.pipe(
			ofType(fetchUniCourses),
			withLatestFrom(this._store.select(getStateUniCourseSearchPayload)),
			switchMap(([, payload]) => this._courseSrv.getUniversityCourses(payload)),
			map((uni) => updateUniCourses({ uni })),
			catchError((error) => {
				const code = error.networkError.error?.data?.code;

				this._notFoundErrorHandler.handleError(code);
				if (code === GraphQLErrorCode.UNI_COURSES_404) {
					return of(updateNotFoundError(error));
				}
				return of(updateCourseSearchError({ error }));
			}),
		);
	});

	fetchPaginatedUniCourses = createEffect(() => {
		return this._actions$.pipe(
			ofType(fetchPaginatedUniCourses),
			withLatestFrom(this._store.select(getStateUniCourseSearchPayload)),
			switchMap(([, payload]) => this._courseSrv.getUniversityCourses(payload)),
			map((uni) => updatePaginatedUniCourses({ uni })),
			catchError((error) => {
				const code = error.networkError.error?.data?.code;

				this._notFoundErrorHandler.handleError(code);
				if (code === GraphQLErrorCode.UNI_COURSES_404) {
					return of(updateNotFoundError(error));
				}
				return of(updateCourseSearchError({ error }));
			}),
		);
	});

	fetchClearingCourses = createEffect(() => {
		return this._actions$.pipe(
			ofType(fetchClearingCourses),
			withLatestFrom(this._store.select(getStateClearingCourseSearchPayload)),
			switchMap(([, payload]) => this._courseSrv.getClearingCourseList(payload)),
			map((clearingCourses) => updateClearingCourses({ clearingCourses })),
			catchError((error) => {
				const code = error.networkError?.error?.data?.code;

				this._notFoundErrorHandler.handleError(code, true);
				if (code === GraphQLErrorCode.UNI_CLEARING_COURSES_404) {
					return of(updateNotFoundError(error));
				}
				return of(updateCourseSearchError({ error }));
			}),
		);
	});

	fetchPaginatedClearingCourses = createEffect(() => {
		return this._actions$.pipe(
			ofType(fetchPaginatedClearingCourses),
			withLatestFrom(this._store.select(getStateClearingCourseSearchPayload)),
			switchMap(([, payload]) => this._courseSrv.getClearingCourseList(payload)),
			map((clearingCourses) => updatePaginatedClearingCourses({ clearingCourses })),
			catchError((error) => {
				const code = error.networkError?.error?.data?.code;

				this._notFoundErrorHandler.handleError(code, true);
				if (code === GraphQLErrorCode.UNI_CLEARING_COURSES_404) {
					return of(updateNotFoundError(error));
				}
				return of(updateCourseSearchError({ error }));
			}),
		);
	});

	fetchClearingUniCourses = createEffect(() => {
		return this._actions$.pipe(
			ofType(fetchClearingUniCourses),
			withLatestFrom(this._store.select(getStateClearingUniCourseSearchPayload)),
			switchMap(([, payload]) =>
				this._courseSrv.getUniversityClearingCourses(payload),
			),
			map((clearingUni) => updateClearingUniCourses({ clearingUni })),
			catchError((error) => {
				const code = error.networkError.error?.data?.code;

				this._notFoundErrorHandler.handleError(code, true);
				if (code === GraphQLErrorCode.UNI_CLEARING_COURSES_404) {
					return of(updateNotFoundError(error));
				}
				return of(updateCourseSearchError({ error }));
			}),
		);
	});

	fetchPaginatedClearingUniCourses = createEffect(() => {
		return this._actions$.pipe(
			ofType(fetchPaginatedClearingUniCourses),
			withLatestFrom(this._store.select(getStateClearingUniCourseSearchPayload)),
			switchMap(([, payload]) =>
				this._courseSrv.getUniversityClearingCourses(payload),
			),
			map((clearingUni) => updatePaginatedClearingUniCourses({ clearingUni })),
			catchError((error) => {
				const code = error.networkError.error?.data?.code;

				this._notFoundErrorHandler.handleError(code, true);
				if (code === GraphQLErrorCode.UNI_CLEARING_COURSES_404) {
					return of(updateNotFoundError(error));
				}
				return of(updateCourseSearchError({ error }));
			}),
		);
	});
}
